import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import WgptCustomReportsAPI from '../../api/wgpt/customReports';
import types from '../mutation-types';
import { throwErrorMessage } from '../utils/api';

const state = {
  records: [],
  activeReport: {},
  queryResult: {},
  chatMessages: [],
  chatReportId: null,
  latestChatConfig: null,
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isChatting: false,
    isQuerying: false,
    isFetchingChatHistory: false,
    isResettingChat: false,
    error: null,
  },
};

export const getters = {
  getCustomReports($state) {
    return $state.records;
  },
  getCustomReport: $state => reportId => {
    const [report] = $state.records.filter(
      record => record.id === Number(reportId)
    );
    return report || {};
  },
  getActiveReport($state) {
    return $state.activeReport;
  },
  getQueryResult($state) {
    return $state.queryResult;
  },
  getChatMessages($state) {
    return $state.chatMessages;
  },
  getChatReportId($state) {
    return $state.chatReportId;
  },
  getLatestChatConfig($state) {
    return $state.latestChatConfig;
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
};

export const actions = {
  get: async ({ commit }) => {
    commit(types.SET_WGPT_CUSTOM_REPORT_UI_FLAG, { isFetching: true });
    try {
      const response = await WgptCustomReportsAPI.get();
      commit(types.SET_WGPT_CUSTOM_REPORTS, response.data);
      return response.data;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_CUSTOM_REPORT_UI_FLAG, { isFetching: false });
    }
  },

  show: async ({ commit }, id) => {
    commit(types.SET_WGPT_CUSTOM_REPORT_UI_FLAG, { isFetchingItem: true });
    try {
      const { data } = await WgptCustomReportsAPI.show(id);
      commit(types.ADD_WGPT_CUSTOM_REPORT, data);
      commit(types.SET_WGPT_CUSTOM_REPORT_ACTIVE, data);
      return data;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_CUSTOM_REPORT_UI_FLAG, { isFetchingItem: false });
    }
  },

  create: async ({ commit }, reportObj) => {
    commit(types.SET_WGPT_CUSTOM_REPORT_UI_FLAG, { isCreating: true });
    try {
      const response = await WgptCustomReportsAPI.create(reportObj);
      commit(types.ADD_WGPT_CUSTOM_REPORT, response.data);
      return response.data;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_CUSTOM_REPORT_UI_FLAG, { isCreating: false });
    }
  },

  update: async ({ commit }, { id, ...updateObj }) => {
    commit(types.SET_WGPT_CUSTOM_REPORT_UI_FLAG, { isUpdating: true });
    try {
      const response = await WgptCustomReportsAPI.update(id, updateObj);
      commit(types.EDIT_WGPT_CUSTOM_REPORT, response.data);
      return response.data;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_CUSTOM_REPORT_UI_FLAG, { isUpdating: false });
    }
  },

  delete: async ({ commit }, id) => {
    commit(types.SET_WGPT_CUSTOM_REPORT_UI_FLAG, { isDeleting: true });
    try {
      await WgptCustomReportsAPI.delete(id);
      commit(types.DELETE_WGPT_CUSTOM_REPORT, id);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_CUSTOM_REPORT_UI_FLAG, { isDeleting: false });
    }
  },

  getChatHistory: async ({ commit }, reportId) => {
    commit(types.SET_WGPT_CUSTOM_REPORT_UI_FLAG, {
      isFetchingChatHistory: true,
    });
    try {
      const response = await WgptCustomReportsAPI.chatState(reportId);
      commit(
        types.SET_WGPT_CUSTOM_REPORT_CHAT_MESSAGES,
        response.data.messages || []
      );
      return response.data;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_CUSTOM_REPORT_UI_FLAG, {
        isFetchingChatHistory: false,
      });
    }
  },

  chat: async ({ commit }, { message, currentConfig, reportId }) => {
    commit(types.SET_WGPT_CUSTOM_REPORT_UI_FLAG, {
      isChatting: true,
      error: null,
    });
    commit(types.ADD_WGPT_CUSTOM_REPORT_CHAT_MESSAGE, {
      role: 'user',
      content: message,
      pending: true,
    });
    try {
      await WgptCustomReportsAPI.chat({ message, currentConfig, reportId });
      // 202 Accepted — result arrives via the wgpt_report_builder.completed event.
    } catch (error) {
      commit(types.REMOVE_WGPT_CUSTOM_REPORT_PENDING_CHAT_MESSAGES);
      const code = error?.response?.status === 409 ? 'processing' : 'failed';
      commit(types.SET_WGPT_CUSTOM_REPORT_UI_FLAG, {
        isChatting: false,
        error: code,
      });
    }
  },

  onChatCompleted: (
    { commit, state: $state },
    { reportId, messages, config }
  ) => {
    if ($state.chatReportId !== reportId) return;
    commit(types.SET_WGPT_CUSTOM_REPORT_CHAT_MESSAGES, messages || []);
    commit(types.SET_WGPT_CUSTOM_REPORT_LATEST_CHAT_CONFIG, config || null);
    commit(types.SET_WGPT_CUSTOM_REPORT_UI_FLAG, {
      isChatting: false,
      error: null,
    });
  },
  onReportBuilderFailed: ({ commit, state: $state }, { reportId }) => {
    if ($state.chatReportId !== reportId) return;
    commit(types.REMOVE_WGPT_CUSTOM_REPORT_PENDING_CHAT_MESSAGES);
    commit(types.SET_WGPT_CUSTOM_REPORT_UI_FLAG, {
      isChatting: false,
      error: 'failed',
    });
  },
  handleTimeout: ({ commit }) => {
    commit(types.REMOVE_WGPT_CUSTOM_REPORT_PENDING_CHAT_MESSAGES);
    commit(types.SET_WGPT_CUSTOM_REPORT_UI_FLAG, {
      isChatting: false,
      error: 'timeout',
    });
  },
  resyncChat: async ({ commit, dispatch }, reportId) => {
    await dispatch('getChatHistory', reportId);
    commit(types.SET_WGPT_CUSTOM_REPORT_UI_FLAG, {
      isChatting: false,
      error: null,
    });
  },
  setChatReportId: ({ commit }, reportId) => {
    commit(types.SET_WGPT_CUSTOM_REPORT_CHAT_REPORT_ID, reportId);
  },
  clearError: ({ commit }) => {
    commit(types.SET_WGPT_CUSTOM_REPORT_UI_FLAG, { error: null });
  },

  resetChat: async ({ commit }, reportId) => {
    commit(types.SET_WGPT_CUSTOM_REPORT_UI_FLAG, { isResettingChat: true });
    try {
      await WgptCustomReportsAPI.chatReset(reportId);
      commit(types.SET_WGPT_CUSTOM_REPORT_CHAT_MESSAGES, []);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_CUSTOM_REPORT_UI_FLAG, { isResettingChat: false });
    }
  },

  query: async ({ commit }, { reportId, params }) => {
    commit(types.SET_WGPT_CUSTOM_REPORT_UI_FLAG, { isQuerying: true });
    commit(types.SET_WGPT_CUSTOM_REPORT_QUERY_RESULT, {});
    try {
      const response = await WgptCustomReportsAPI.query(reportId, params);
      commit(types.SET_WGPT_CUSTOM_REPORT_QUERY_RESULT, response.data);
      return response.data;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_CUSTOM_REPORT_UI_FLAG, { isQuerying: false });
    }
  },

  setActiveReport({ commit }, report) {
    commit(types.SET_WGPT_CUSTOM_REPORT_ACTIVE, report);
  },

  clearChatMessages({ commit }) {
    commit(types.SET_WGPT_CUSTOM_REPORT_CHAT_MESSAGES, []);
  },
};

export const mutations = {
  [types.SET_WGPT_CUSTOM_REPORT_UI_FLAG]($state, data) {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },
  [types.SET_WGPT_CUSTOM_REPORTS]: MutationHelpers.set,
  [types.ADD_WGPT_CUSTOM_REPORT]: MutationHelpers.setSingleRecord,
  [types.EDIT_WGPT_CUSTOM_REPORT]: MutationHelpers.update,
  [types.DELETE_WGPT_CUSTOM_REPORT]: MutationHelpers.destroy,
  [types.SET_WGPT_CUSTOM_REPORT_ACTIVE]($state, data) {
    $state.activeReport = data;
  },
  [types.SET_WGPT_CUSTOM_REPORT_QUERY_RESULT]($state, data) {
    $state.queryResult = data;
  },
  [types.SET_WGPT_CUSTOM_REPORT_CHAT_MESSAGES]($state, messages) {
    $state.chatMessages = messages;
  },
  [types.ADD_WGPT_CUSTOM_REPORT_CHAT_MESSAGE]($state, message) {
    $state.chatMessages = [...$state.chatMessages, message];
  },
  [types.SET_WGPT_CUSTOM_REPORT_CHAT_REPORT_ID]($state, reportId) {
    $state.chatReportId = reportId;
  },
  [types.SET_WGPT_CUSTOM_REPORT_LATEST_CHAT_CONFIG]($state, config) {
    $state.latestChatConfig = config;
  },
  [types.REMOVE_WGPT_CUSTOM_REPORT_PENDING_CHAT_MESSAGES]($state) {
    $state.chatMessages = $state.chatMessages.filter(m => !m.pending);
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
